import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { fadeInUp } from '../constants/animations';
import { buildApiUrl } from '../utils/api';
import FooterSection from '../features/home/components/FooterSection';
import './Blog.css';

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(buildApiUrl(`/api/blog/${slug}`))
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          setLoading(false);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) {
          setPost(data);
          setLoading(false);
        }
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // AI-generated posts ship as semantic HTML; older posts use a **heading**/newline format.
  const renderContent = (content) => {
    if (!content) return '';
    const trimmed = content.trim();
    const looksLikeHtml = /<(h2|h3|p|ul|ol|blockquote|article|section|div)\b/i.test(trimmed);
    if (looksLikeHtml) return trimmed;

    return trimmed
      .replace(/\*\*(.*?)\*\*/g, '<h2>$1</h2>')
      .split('\n')
      .map((line) => (line.trim().startsWith('<h2>') ? line : `<p>${line}</p>`))
      .join('');
  };

  const estimateReadMinutes = (content) => {
    if (!content) return 0;
    const text = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = text ? text.split(' ').length : 0;
    return Math.max(1, Math.round(words / 230));
  };

  if (loading) {
    return (
      <main className="blog-page">
        <div className="blog-loading">
          <div className="blog-spinner" />
        </div>
        <FooterSection />
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="blog-page">
        <div className="blog-not-found container">
          <h1>Post not found</h1>
          <Link to="/blog" className="blog-back-link">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>
        <FooterSection />
      </main>
    );
  }

  return (
    <main className="blog-page">
      <Helmet>
        <title>{post.title} | Versa Launch</title>
        <meta name="description" content={post.meta_description} />
      </Helmet>

      <motion.article className="blog-post-article" initial="hidden" animate="visible">
        {post.hero_image_url && (
          <div className="blog-post-hero-image">
            <img src={post.hero_image_url} alt={post.title} />
          </div>
        )}

        <div className="container blog-post-container">
          <motion.div variants={fadeInUp} custom={0}>
            <Link to="/blog" className="blog-back-link">
              <ArrowLeft size={16} /> Back to Blog
            </Link>
          </motion.div>

          <motion.div className="blog-post-meta" variants={fadeInUp} custom={1}>
            <span className="blog-post-meta-item">
              <Calendar size={14} />
              {formatDate(post.published_at)}
            </span>
            <span className="blog-post-meta-divider" aria-hidden="true">•</span>
            <span className="blog-post-meta-item">
              <Clock size={14} />
              {estimateReadMinutes(post.content)} min read
            </span>
          </motion.div>

          <motion.h1 variants={fadeInUp} custom={2}>{post.title}</motion.h1>

          {post.meta_description && (
            <motion.p className="blog-post-lead" variants={fadeInUp} custom={2.5}>
              {post.meta_description}
            </motion.p>
          )}

          <motion.div
            className="blog-post-content"
            variants={fadeInUp}
            custom={3}
            dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
          />
        </div>
      </motion.article>

      <FooterSection />
    </main>
  );
}

export default BlogPost;
